/**
 * avatars/aliens/alien-presets-data.js
 * Danh mục 30 nhân vật đại diện hành tinh (ALIEN_PRESETS).
 * Kế thừa trực tiếp từ Nguồn Sự Thật Duy Nhất (Single Source of Truth - CIVILIZED_SPECIES)
 * qua hàm tạo chuẩn buildAlienPreset. Thuần dữ liệu, không phụ thuộc DOM/Three.js.
 */

import { CIVILIZED_SPECIES } from './species-data.js';
import { getSpeciesPopulationMember } from './population-generator.js';
import { ALIEN_PRESET_IDENTITIES } from './alien-presets-identity.js';
import {
  SPECIES_IGNIS,
  SPECIES_CYCLOPIA,
  SPECIES_BIONIC,
  SPECIES_CRYO,
  SPECIES_OCEAN,
  SPECIES_MYCELIUM,
  SPECIES_SYNAPSE,
  SPECIES_AURORA,
  SPECIES_OSSIFIED,
  SPECIES_QUANTUM,
  SPECIES_DRAKONIS,
  SPECIES_SYLVA,
  SPECIES_INSECT,
  SPECIES_VENOM,
  SPECIES_DUNE,
  SPECIES_SERAPH,
  SPECIES_LITHOS,
  SPECIES_GEM,
  SPECIES_MERCURY,
  SPECIES_MIDAS,
  SPECIES_STEAMPUNK,
  SPECIES_FULGOR,
  SPECIES_ASTRAL,
  SPECIES_ZEPHYR,
  SPECIES_RADIATION,
  SPECIES_GRAVITON,
  SPECIES_CHRONO,
  SPECIES_NEBULA,
  SPECIES_VOID,
  SPECIES_SINGULARITY
} from './species-ids.js';

/**
 * Hàm khởi tạo Alien Preset kế thừa trực tiếp từ nguồn sự thật CIVILIZED_SPECIES và bản sắc danh tính
 * @param {string} speciesKey - Mã chủng tộc trong CIVILIZED_SPECIES
 * @param {string} presetKey - Mã định danh Preset trong ALIEN_PRESET_IDENTITIES
 * @param {object} presetOverrides - Các giá trị ghi đè cá nhân hóa (id, gender, age, scale, phụ kiện...)
 */
export function buildAlienPreset(speciesKey, presetKey, presetOverrides = {}) {
  const sp = CIVILIZED_SPECIES[speciesKey];
  if (!sp) {
    throw new Error(`Species key '${speciesKey}' không tồn tại trong CIVILIZED_SPECIES`);
  }
  const ident = ALIEN_PRESET_IDENTITIES[presetKey] || {};
  const gender = presetOverrides.gender || 'male';
  const age = presetOverrides.age || 'adult';
  const member = getSpeciesPopulationMember(speciesKey, { gender, age });
  const defFeatures = member.features;
  const defColors = member.colors;

  const { features: overrideFeatures, colors: overrideColors, ...restOverrides } = presetOverrides;

  return {
    speciesKey,
    id: presetOverrides.id || `alien_${speciesKey.replace('species_', '')}`,
    name: presetOverrides.name || ident.name || sp.name,
    title: presetOverrides.title || ident.title || `${sp.planet} • ${sp.name}`,
    planet: sp.planet,
    species: sp.name,
    category: presetOverrides.category || 'Cư dân văn minh',
    gender,
    age,
    scale: presetOverrides.scale ?? member.scale ?? 1.0,
    bodyType: presetOverrides.bodyType || member.bodyType || 'normal',
    defaultReactions: presetOverrides.defaultReactions || ident.defaultReactions || sp.reactions,
    ...restOverrides,
    colors: {
      ...defColors,
      skin: sp.dna.skinColor,
      ...(overrideColors || {})
    },
    features: {
      ...defFeatures,
      horns: sp.dna.horns || 'none',
      wings: sp.dna.wings || 'none',
      tail: sp.dna.tail || 'none',
      eyeType: sp.dna.eyeType || 'normal',
      outfit: gender === 'female' ? sp.clothing.femaleOutfit : sp.clothing.maleOutfit,
      heldItem: 'none',
      ...(overrideFeatures || {})
    }
  };
}

export const ALIEN_PRESETS = {
  // 1. Người Lửa Nham Thạch (Ignis Prime)
  ALIEN_IGNIS_HORN: buildAlienPreset(SPECIES_IGNIS, 'ALIEN_IGNIS_HORN', {
    id: 'alien_ignis_horn',
    scale: 1.05
  }),

  // 2. Thiên Sứ Ánh Sáng (Aethelgard)
  ALIEN_SERAPH_WING: buildAlienPreset(SPECIES_SERAPH, 'ALIEN_SERAPH_WING', {
    id: 'alien_seraph_wing',
    scale: 1.02,
    bodyType: 'tall_lean'
  }),

  // 3. Người Độc Nhãn Tinh Cầu (Cyclopia IV)
  ALIEN_CYCLOPS_EYE: buildAlienPreset(SPECIES_CYCLOPIA, 'ALIEN_CYCLOPS_EYE', {
    id: 'alien_cyclops_eye',
    scale: 1.06
  }),

  // 4. Cư Dân Bionic Cơ Giới (Neon-Synthetica)
  ALIEN_CYBORG_MECHA: buildAlienPreset(SPECIES_BIONIC, 'ALIEN_CYBORG_MECHA', {
    id: 'alien_cyborg_mecha',
    scale: 1.02,
    features: {
      bodyAccessory: 'radiation_core'
    }
  }),

  // 5. Cư Dân Băng Giá (Cryonia)
  ALIEN_CRYO_FROST: buildAlienPreset(SPECIES_CRYO, 'ALIEN_CRYO_FROST', {
    id: 'alien_cryo_frost',
    scale: 1.04
  }),

  // 6. Cư Dân Đại Dương Biển Sâu (Aquaterra)
  ALIEN_AQUALUX: buildAlienPreset(SPECIES_OCEAN, 'ALIEN_AQUALUX', {
    id: 'alien_aqualux',
    scale: 1.02
  }),

  // 7. Tinh Linh Rừng Già (Sylva Prime)
  ALIEN_SYLVA_FLORA: buildAlienPreset(SPECIES_SYLVA, 'ALIEN_SYLVA_FLORA', {
    id: 'alien_sylva_flora',
    scale: 1.0
  }),

  // 8. Cư Dân Hư Không (Umbra-9)
  ALIEN_VOID_SHADE: buildAlienPreset(SPECIES_VOID, 'ALIEN_VOID_SHADE', {
    id: 'alien_void_shade',
    scale: 1.02
  }),

  // 9. Cư Dân Tinh Thể Pha Lê (Krystalia)
  ALIEN_GEM_CRYSTAL: buildAlienPreset(SPECIES_GEM, 'ALIEN_GEM_CRYSTAL', {
    id: 'alien_gem_crystal',
    scale: 1.03
  }),

  // 10. Chiến Binh Độc Tố Huỳnh Quang (Venom-Ooze)
  ALIEN_VENOM_TOXIS: buildAlienPreset(SPECIES_VENOM, 'ALIEN_VENOM_TOXIS', {
    id: 'alien_venom_toxis',
    scale: 1.0
  }),

  // 11. Cư Dân Kháng Trọng Lực (Baro-Prime)
  ALIEN_GRAVITON: buildAlienPreset(SPECIES_GRAVITON, 'ALIEN_GRAVITON', {
    id: 'alien_graviton',
    scale: 1.08,
    bodyType: 'muscular',
    features: {
      bodyAccessory: 'radiation_core'
    }
  }),

  // 12. Cư Dân Gió Tầng Không (Aetheria)
  ALIEN_ZEPHYR_STRIDER: buildAlienPreset(SPECIES_ZEPHYR, 'ALIEN_ZEPHYR_STRIDER', {
    id: 'alien_zephyr_strider',
    scale: 1.04,
    bodyType: 'tall_lean'
  }),

  // 13. Cư Dân Hồ Quang Điện (Voltaria)
  ALIEN_FULGOR_SPARK: buildAlienPreset(SPECIES_FULGOR, 'ALIEN_FULGOR_SPARK', {
    id: 'alien_fulgor_spark',
    scale: 1.02
  }),

  // 14. Cư Dân Thủy Ngân Kim Loại Lỏng (Hydrargis)
  ALIEN_MERCURY_METAL: buildAlienPreset(SPECIES_MERCURY, 'ALIEN_MERCURY_METAL', {
    id: 'alien_mercury_metal',
    scale: 1.04
  }),

  // 15. Cư Dân Mạng Nấm Phát Quang (Fungalia)
  ALIEN_FUNGAL_SPORE: buildAlienPreset(SPECIES_MYCELIUM, 'ALIEN_FUNGAL_SPORE', {
    id: 'alien_fungal_spore',
    scale: 0.98
  }),

  // 16. Cư Dân Bụi Sao Tinh Tú (Stardust-Nova)
  ALIEN_ASTRAL_ENERGY: buildAlienPreset(SPECIES_ASTRAL, 'ALIEN_ASTRAL_ENERGY', {
    id: 'alien_astral_energy',
    scale: 1.04,
    bodyType: 'tall_lean'
  }),

  // 17. Du Mục Sa Mạc Cát Vàng (Dune-Solaris)
  ALIEN_DUNE_NOMAD: buildAlienPreset(SPECIES_DUNE, 'ALIEN_DUNE_NOMAD', {
    id: 'alien_dune_nomad',
    scale: 1.0,
    features: {
      hat: 'mu_cao_boi',
      eyewear: 'kinh_phi_cong',
      mask: 'khau_trang_den'
    }
  }),

  // 18. Học Giả Cổ Tự Đá Phiến (Archaea-Rune)
  ALIEN_RUNE_GOLEM: buildAlienPreset(SPECIES_LITHOS, 'ALIEN_RUNE_GOLEM', {
    id: 'alien_rune_golem',
    scale: 1.06,
    bodyType: 'muscular'
  }),

  // 19. Kỹ Sư Năng Lượng Bức Xạ (Radia-66)
  ALIEN_RAD_MUTANT: buildAlienPreset(SPECIES_RADIATION, 'ALIEN_RAD_MUTANT', {
    id: 'alien_rad_mutant',
    scale: 1.02,
    features: {
      bodyAccessory: 'radiation_core'
    }
  }),

  // 20. Người Canh Giữ Thời Gian (Chronopolis)
  ALIEN_CHRONO_KEEPER: buildAlienPreset(SPECIES_CHRONO, 'ALIEN_CHRONO_KEEPER', {
    id: 'alien_chrono_keeper',
    scale: 1.02
  }),

  // 21. Cư Dân Hoàng Kim (Aurum-Vega) - Đồng bộ theo species_midas (SSOT)
  ALIEN_MIDAS_GOLD: buildAlienPreset(SPECIES_MIDAS, 'ALIEN_MIDAS_GOLD', {
    id: 'alien_midas_gold',
    scale: 1.04,
    features: {
      hat: 'crown_gold'
    }
  }),

  // 22. Nhà Nghiên Cứu Lỗ Đen (Singularis) - Đồng bộ theo species_singularity (SSOT)
  ALIEN_EVENT_HORIZON: buildAlienPreset(SPECIES_SINGULARITY, 'ALIEN_EVENT_HORIZON', {
    id: 'alien_event_horizon',
    scale: 1.02,
    features: {
      bodyAccessory: 'accretion_ring'
    }
  }),

  // 23. Cư Dân Tâm Trí Đồng Thuận (Synapse-Net)
  ALIEN_SYNAPSE_MIND: buildAlienPreset(SPECIES_SYNAPSE, 'ALIEN_SYNAPSE_MIND', {
    id: 'alien_synapse_mind',
    scale: 0.98,
    bodyType: 'tall_lean'
  }),

  // 24. Kỹ Sư Cơ Khí Bánh Răng Đồng (Steam-Cog)
  ALIEN_STEAMPUNK_PILOT: buildAlienPreset(SPECIES_STEAMPUNK, 'ALIEN_STEAMPUNK_PILOT', {
    id: 'alien_steampunk_pilot',
    scale: 1.0,
    features: {
      hat: 'mu_phi_cong',
      eyewear: 'kinh_han'
    }
  }),

  // 25. Tiên Nữ Cực Quang (Borealis-V) - Giới tính Nữ
  ALIEN_AURORA_FAERIE: buildAlienPreset(SPECIES_AURORA, 'ALIEN_AURORA_FAERIE', {
    id: 'alien_aurora_faerie',
    gender: 'female',
    scale: 0.96
  }),

  // 26. Cư Dân Tinh Vân Vũ Trụ (Nebula-Prime)
  ALIEN_COSMIC_NEBULA: buildAlienPreset(SPECIES_NEBULA, 'ALIEN_COSMIC_NEBULA', {
    id: 'alien_cosmic_nebula',
    scale: 1.02
  }),

  // 27. Người Điêu Khắc Khung Xương (Ossuary-Prime)
  ALIEN_BONE_CARVER: buildAlienPreset(SPECIES_OSSIFIED, 'ALIEN_BONE_CARVER', {
    id: 'alien_bone_carver',
    scale: 1.05,
    bodyType: 'muscular'
  }),

  // 28. Phi Hành Gia Lượng Tử (Quantum-X)
  ALIEN_QUANTUM_ASTRONAUT: buildAlienPreset(SPECIES_QUANTUM, 'ALIEN_QUANTUM_ASTRONAUT', {
    id: 'alien_quantum_astronaut',
    scale: 1.02
  }),

  // 29. Nhân Loại Côn Trùng Giáp Kitin (Entomo-Hive)
  ALIEN_INSECT_HUMAN: buildAlienPreset(SPECIES_INSECT, 'ALIEN_INSECT_HUMAN', {
    id: 'alien_insect_human',
    scale: 1.0
  }),

  // 30. Long Tộc Hỏa Long Cổ Đại (Drakonis)
  ALIEN_DRAGON_BLOOD: buildAlienPreset(SPECIES_DRAKONIS, 'ALIEN_DRAGON_BLOOD', {
    id: 'alien_dragon_blood',
    scale: 1.08,
    bodyType: 'muscular'
  })
};
